"use client";

import { useState } from "react";
import {
  Checkbox,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  NumberResult,
  type ResultLine,
} from "@/components/tools/numbers-languages/number-result";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type GermanNote,
  type GermanOne,
  type GermanOrdinalEnding,
  germanNumber,
  germanOrdinal,
  MAX_GERMAN_ORDINAL,
} from "@/lib/tool-kit/numbers-languages/german";
import {
  type DecimalNumber,
  type ReadError,
  readDecimalNumber,
} from "@/lib/tool-kit/numbers-languages/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const GERMAN_NUMBER_CONVERTER_STRINGS = {
  numberLabel: "Number",
  numberHint:
    "Whole numbers up to 24 digits; a decimal comma or point, a minus sign and spaces between digit groups are fine.",
  numberPlaceholder: "2026",
  modeLabel: "Spell it as",
  modeCardinal: "Cardinal (eins, zwei, drei)",
  modeOrdinal: "Ordinal (erste, zweite)",
  oneLabel: "A final 1 is written",
  oneEins: "eins (counting)",
  oneEin: "ein (der Tag, das Jahr)",
  oneEine: "eine (die Woche)",
  oneEinen: "einen (accusative: einen Tag)",
  endingLabel: "Ordinal ending",
  endingE: "-e (der erste Tag, die erste Woche)",
  endingEr: "-er (ein erster Tag, des ersten)",
  endingEs: "-es (ein erstes Jahr)",
  endingEn: "-en (den ersten Tag)",
  endingEm: "-em (dem ersten Tag)",
  einPrefix: "Write einhundert and eintausend (not hundert, tausend)",
  swiss: "Swiss spelling (ss instead of ß)",
  resultHeading: "In German",
  copy: "Copy words",
  copied: "Copied!",
  abbreviationLabel: "Written with digits",
  alsoLabel: "Also correct",
  rulesHeading: "Why it is spelled this way",
  enterPrompt: "Type a number to see it spelled out.",
  errorInvalid:
    "That is not a number. Use digits, with an optional minus sign and decimal comma or point.",
  errorTooBig: "Numbers are limited to 24 digits (below 10²⁴).",
  errorTooLong: "Keep the decimal part to 20 digits or fewer.",
  errorOrdinalRange:
    "Ordinals are supported for whole numbers from 1 to {max}.",
  noteOneWord:
    "Numbers below a million are written as one word in lower case, however long: hundertdreiundzwanzigtausend.",
  noteUnitFirst:
    "The unit comes before the ten, joined with “und”: einundzwanzig, not zwanzigeins.",
  noteEinPrefix:
    "“Ein-” before hundert and tausend is optional in the Duden: einhundert and hundert are both correct.",
  noteOneForm:
    "A final 1 is “eins” when counting and “ein”, “eine” or “einen” before a noun. The 1 inside einundzwanzig is always “ein”.",
  noteMillionApart:
    "Million, Milliarde and the larger names are capitalised nouns written apart: eine Million zweihunderttausend, zwei Millionen.",
  noteLongScale:
    "German uses the long scale: eine Milliarde is 10⁹ and eine Billion is 10¹², not the US billion or trillion.",
  noteSwiss: "In Switzerland and Liechtenstein ß is written ss: dreissig.",
} as const;

const DEFAULT_NUMBER = "2026";

type Strings = typeof GERMAN_NUMBER_CONVERTER_STRINGS;
type Mode = "cardinal" | "ordinal";

const NOTE_KEYS: Record<GermanNote, keyof Strings> = {
  oneWord: "noteOneWord",
  unitFirst: "noteUnitFirst",
  einPrefix: "noteEinPrefix",
  oneForm: "noteOneForm",
  millionApart: "noteMillionApart",
  longScale: "noteLongScale",
  swiss: "noteSwiss",
};

interface Controls {
  mode: Mode;
  one: GermanOne;
  ending: GermanOrdinalEnding;
  einPrefix: boolean;
  swiss: boolean;
}

interface View {
  words: string;
  abbreviation?: string;
  lines: ResultLine[];
  notes: string[];
}

type Outcome = { view: View } | { error: string } | null;

function errorText(error: ReadError, strings: Strings): string | null {
  switch (error) {
    case "empty":
      return null;
    case "tooBig":
      return strings.errorTooBig;
    case "tooLong":
      return strings.errorTooLong;
    default:
      return strings.errorInvalid;
  }
}

function cardinalView(
  value: DecimalNumber,
  controls: Controls,
  strings: Strings
): View {
  const result = germanNumber(
    value.negative,
    value.integer,
    value.fraction,
    controls
  );
  return {
    words: result.words,
    lines: [],
    notes: result.notes.map((note) => strings[NOTE_KEYS[note]]),
  };
}

function ordinalOutcome(
  value: DecimalNumber,
  controls: Controls,
  strings: Strings
): Outcome {
  const inRange = !value.negative && value.fraction === "";
  const ordinal = inRange
    ? germanOrdinal(Number(value.integer), controls)
    : null;
  if (ordinal === null) {
    return {
      error: t(strings.errorOrdinalRange, {
        max: String(MAX_GERMAN_ORDINAL),
      }),
    };
  }
  return {
    view: {
      words: ordinal.words,
      abbreviation: ordinal.abbreviation,
      lines: ordinal.alternative
        ? [{ label: strings.alsoLabel, text: ordinal.alternative, lang: "de" }]
        : [],
      notes: [],
    },
  };
}

function evaluate(raw: string, controls: Controls, strings: Strings): Outcome {
  const read = readDecimalNumber(raw);
  if (!read.ok) {
    const message = errorText(read.error, strings);
    return message === null ? null : { error: message };
  }
  if (controls.mode === "ordinal") {
    return ordinalOutcome(read.value, controls, strings);
  }
  return { view: cardinalView(read.value, controls, strings) };
}

export default function GermanNumberConverterWidget() {
  const strings = useToolUi(GERMAN_NUMBER_CONVERTER_STRINGS);
  const [raw, setRaw] = useState(DEFAULT_NUMBER);
  const [controls, setControls] = useState<Controls>({
    mode: "cardinal",
    one: "eins",
    ending: "e",
    einPrefix: false,
    swiss: false,
  });
  const set = <K extends keyof Controls>(key: K, value: Controls[K]) =>
    setControls((current) => ({ ...current, [key]: value }));
  const outcome = evaluate(raw, controls, strings);

  return (
    <div className="space-y-6">
      <TextField
        hint={strings.numberHint}
        id="de-number"
        label={strings.numberLabel}
        numeric
        onChange={setRaw}
        placeholder={strings.numberPlaceholder}
        value={raw}
      />
      <PillGroup
        label={strings.modeLabel}
        name="de-mode"
        onChange={(value) => set("mode", value)}
        options={[
          { value: "cardinal", label: strings.modeCardinal },
          { value: "ordinal", label: strings.modeOrdinal },
        ]}
        value={controls.mode}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {controls.mode === "cardinal" ? (
          <SelectField
            id="de-one"
            label={strings.oneLabel}
            onChange={(value) => set("one", value)}
            options={[
              { value: "eins", label: strings.oneEins },
              { value: "ein", label: strings.oneEin },
              { value: "eine", label: strings.oneEine },
              { value: "einen", label: strings.oneEinen },
            ]}
            value={controls.one}
          />
        ) : (
          <SelectField
            id="de-ending"
            label={strings.endingLabel}
            onChange={(value) => set("ending", value)}
            options={[
              { value: "e", label: strings.endingE },
              { value: "er", label: strings.endingEr },
              { value: "es", label: strings.endingEs },
              { value: "en", label: strings.endingEn },
              { value: "em", label: strings.endingEm },
            ]}
            value={controls.ending}
          />
        )}
        <div className="flex flex-col justify-end gap-2">
          <Checkbox
            checked={controls.einPrefix}
            label={strings.einPrefix}
            onChange={(value) => set("einPrefix", value)}
          />
          <Checkbox
            checked={controls.swiss}
            label={strings.swiss}
            onChange={(value) => set("swiss", value)}
          />
        </div>
      </div>
      {outcome === null ? <Notice>{strings.enterPrompt}</Notice> : null}
      {outcome !== null && "error" in outcome ? (
        <ErrorNotice>{outcome.error}</ErrorNotice>
      ) : null}
      {outcome !== null && "view" in outcome ? (
        <NumberResult
          detail={
            outcome.view.abbreviation
              ? {
                  label: strings.abbreviationLabel,
                  text: outcome.view.abbreviation,
                }
              : undefined
          }
          lang="de"
          lines={outcome.view.lines}
          notes={outcome.view.notes}
          strings={{
            heading: strings.resultHeading,
            copy: strings.copy,
            copied: strings.copied,
            rulesHeading: strings.rulesHeading,
          }}
          words={outcome.view.words}
        />
      ) : null}
    </div>
  );
}
